<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" lg="10" offset-lg="1">
        <h2 class="title mb-3">固定ページ管理</h2>
        <permission-guard situation="media_page_management">
          <v-btn
            color="primary"
            dark
            tile
            class="ma-2"
            @click="initializeMediaPage"
          >
            新規作成
          </v-btn>
          <v-simple-table fixed-header>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">タイトル</th>
                  <th class="text-left">slug</th>
                  <th class="text-left">URL</th>
                  <th class="text-left">最終更新日</th>
                  <th />
                  <th />
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr v-for="page in mediaPages" :key="page.id">
                  <td class="text-left">
                    {{ page.title }}
                  </td>
                  <td class="text-left">
                    {{ page.slug }}
                  </td>
                  <td class="text-left">
                    {{ page.url }}
                  </td>
                  <td>
                    {{ page.updatedAt | moment }}
                  </td>
                  <td class="text-center">
                    <template v-if="page.isPublished">
                      <v-chip
                        class="ma-2 font-weight-bold"
                        x-small
                        label
                        color="success"
                      >
                        公開
                      </v-chip>
                    </template>
                    <template v-else>
                      <v-chip
                        class="ma-2 font-weight-bold"
                        x-small
                        label
                        color="warning"
                      >
                        下書き
                      </v-chip>
                    </template>
                  </td>
                  <td class="text-center">
                    <v-btn
                      tile
                      outlined
                      small
                      color="primary"
                      @click="editMediaPage(page)"
                    >
                      <v-icon left small> mdi-pencil </v-icon>
                      EDIT
                    </v-btn>
                  </td>
                  <td class="text-center">
                    <v-btn
                      tile
                      outlined
                      small
                      color="danger"
                      @click="deleteMediaPage(page)"
                    >
                      <v-icon left small> mdi-delete </v-icon>DELETE
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <DataLoader :loading="loading" />
        </permission-guard>
      </v-col>
    </v-row>
    <v-dialog v-model="editDialog" width="800">
      <v-card>
        <v-card-title />
        <v-card-text>
          <v-form>
            <v-text-field
              v-if="mediaPage.id"
              v-model="mediaPage.variety"
              label="ページの種類"
              readonly
              disabled
            />
            <v-text-field v-model="mediaPage.title" label="タイトル" required />
            <v-text-field
              v-model="mediaPage.slug"
              label="slug"
              hint="urlの名称になります。半角英字推奨"
              required
            />
            <v-text-field
              v-model="mediaPage.url"
              label="URL"
              readonly
              disabled
            />
            <v-switch
              v-model="mediaPage.isPublished"
              persistent-hint
              class="p-settingForm--inputSwitch mb-5"
              hint="OFFにした場合は下書き"
              label="公開する"
            />
            <v-tabs v-model="tab" fixed-tabs class='pb-5'>
              <v-tab>簡単エディター</v-tab>
              <v-tab>HTMLエディター</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item>
                <tiptap-editor
                  :content="mediaPage.content"
                  @change="reflectContent"
                />
              </v-tab-item>
              <v-tab-item>
                <codemirror v-model="mediaPage.content" :options="cmOptions" />
              </v-tab-item>
            </v-tabs-items>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="mediaPage.id"
            color="green darken-1"
            text
            @click="updateMediaPage"
          >
            保存
          </v-btn>
          <v-btn v-else color="green darken-1" text @click="createMediaPage">
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import TiptapEditor from '@/components/commons/TiptapEditor'
import headUtilsMixin from '@/mixins/head_utils'
import PermissionGuard from '@/components/commons/PermissionGuard'
import { deserialize } from 'deserialize-json-api'
import DataLoader from '@/components/commons/DataLoader'

export default {
  name: 'MediaPages',
  components: {
    TiptapEditor,
    PermissionGuard,
    DataLoader
  },
  filters: {
    moment: function (date) {
      return moment(date).format('YYYY年M月D日 HH時mm分')
    }
  },
  mixins: [headUtilsMixin],
  data: () => {
    return {
      pageTitle: '固定ページ一覧',
      mediaPages: [],
      mediaPage: {},
      editDialog: false,
      loading: true,
      tab: null,
      cmOptions: {
        mode: 'text/html',
        lineNumbers: true,
        theme: 'monokai',
        autoCloseTags: true,
        showCursorWhenSelecting: true
      }
    }
  },
  mounted: function () {
    axios
      .get('/api/media_pages')
      .then(
        ({ data }) => {
          this.mediaPages = deserialize(data).data
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: `固定ページの取得に失敗しました（${error.response.data.errors.toString()}）`
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
      .finally(() => {
        this.loading = false
      })
  },
  methods: {
    reflectContent (value) {
      this.mediaPage.content = value
    },
    editMediaPage (mediaPage) {
      this.mediaPage = { ...mediaPage }
      this.editDialog = true
    },
    initializeMediaPage () {
      this.mediaPage = {
        variety: 'test', // varietyカラム削除時に消す
        title: '',
        url: '',
        content: '',
        slug: '',
        isPublished: false
      }
      this.editDialog = true
    },
    createMediaPage () {
      const query = {
        media_page: {
          variety: this.mediaPage.variety,
          title: this.mediaPage.title,
          url: this.mediaPage.url,
          content: this.mediaPage.content,
          slug: this.mediaPage.slug,
          isPublished: this.mediaPage.isPublished
        }
      }
      axios.post(`/api/media_pages`, query).then(
        ({ data }) => {
          this.mediaPages.push(deserialize(data).data)
          this.editDialog = false
          const payload = {
            status: 200,
            message: '変更を保存しました'
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.message.toString()
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
    },
    updateMediaPage () {
      const query = {
        media_page: {
          title: this.mediaPage.title,
          url: this.mediaPage.url,
          content: this.mediaPage.content,
          slug: this.mediaPage.slug,
          isPublished: this.mediaPage.isPublished
        }
      }
      axios.patch(`/api/media_pages/${this.mediaPage.id}`, query).then(
        ({ data }) => {
          this.editDialog = false
          const payload = {
            status: 200,
            message: '変更を保存しました'
          }
          const updatedMediaPage = data.data.attributes
          Object.assign(
            this.mediaPages.find((list) => list.id === updatedMediaPage.id),
            updatedMediaPage
          )
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.message.toString()
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
    },
    deleteMediaPage (mediaPage) {
      if (confirm('本当に削除しますか？')) {
        axios.delete(`/api/media_pages/${mediaPage.id}`).then(
          () => {
            const index = this.mediaPages.findIndex(
              (element) => element.id === mediaPage.id
            )
            this.mediaPages.splice(index, 1)
            const payload = {
              status: 200,
              message: '削除しました。'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.errors.toString()
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
      }
    }
  }
}
</script>

<style scoped>
th,
td {
  vertical-align: middle;
}
</style>
